import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { VendorPlan, VendorPlanOverrides } from "../../our-vendors/models/vendor.model";

// Limits the panel can override. maxProducts/maxSpaces are intentionally
// excluded — they keep their dedicated vendor override fields for now.
// Labels: ADMIN_VENDOREDIT.OVERRIDES.LIMIT.<key>.
const LIMIT_FIELDS: string[] = [
    "maxEmailsPerMonth",
    "maxSyncFlowsPerDay",
    "maxAutomationRules",
    "maxLeads",
    "maxImportProducts",
    "monthlyCredits",
    "maxSocialAccounts",
    "maxScheduledPostsPerMonth",
    "maxVisualizationsPerMonth",
    "maxSeats",
];

// Labels: ADMIN_VENDOREDIT.OVERRIDES.FEATURE.<key>.
const FEATURE_FIELDS: string[] = [
    "apiIntegration",
    "emailAutomation",
    "aiSpaceAnalysis",
    "seamlessGeneration",
    "model3dGeneration",
    "advancedAnalytics",
    "customFields",
    "categoryManagement",
    "productCardCustomization",
    "contentStudio",
    "socialScheduler",
    "shopperChat",
    "adsManager",
    "aiProductEnrichment",
    // Granted per vendor, never by plan: the visualizer's design assistant.
    "smartAssistant",
    // Granted per vendor, never by plan: carpets placed as custom-size rugs in the visualizer.
    "customSizeRugs",
];

const QUALITIES = ["low", "medium", "high"] as const;

interface Props {
    /** The currently-selected base plan (source of inherited values). */
    basePlan?: VendorPlan;
    /** Current per-vendor overrides. */
    value?: VendorPlanOverrides;
    /** Emits the pruned overrides object, or undefined when nothing is overridden. */
    onChange: (next: VendorPlanOverrides | undefined) => void;
}

// Drop empty/undefined keys; return undefined when the object has no real entries.
function prune<T extends Record<string, any>>(obj?: T): T | undefined {
    if (!obj) return undefined;
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(obj)) {
        if (v !== null && v !== undefined) out[k] = v;
    }
    return Object.keys(out).length ? (out as T) : undefined;
}

export const VendorPlanOverridesPanel = ({ basePlan, value, onChange }: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const limits = value?.limits || {};
    const features = value?.features || {};
    const overrideCount =
        Object.keys(prune(limits) || {}).length + Object.keys(prune(features) || {}).length;

    const emit = (nextLimits: Record<string, any>, nextFeatures: Record<string, any>) => {
        const l = prune(nextLimits);
        const f = prune(nextFeatures);
        const result: VendorPlanOverrides = {};
        if (l) result.limits = l;
        if (f) result.features = f;
        onChange(Object.keys(result).length ? result : undefined);
    };

    const setLimit = (key: string, val: number | undefined) => {
        const next = { ...limits } as Record<string, any>;
        if (val === undefined) delete next[key];
        else next[key] = val;
        emit(next, features);
    };

    const setFeature = (key: string, val: boolean | string | undefined) => {
        const next = { ...features } as Record<string, any>;
        if (val === undefined) delete next[key];
        else next[key] = val;
        emit(limits, next);
    };

    const inheritedLimit = (key: string): number | undefined => basePlan?.limits?.[key];
    const inheritedFeature = (key: string): boolean | undefined => basePlan?.features?.[key];

    const inherits = (value: string) => t("ADMIN_VENDOREDIT.OVERRIDES.INHERITS", { value });
    const qualityLabel = (q?: string) =>
        q && (QUALITIES as readonly string[]).includes(q) ? t(`ADMIN_VENDOREDIT.OVERRIDES.QUALITY_${q.toUpperCase()}`) : "—";

    return (
        <div className="rounded-lg border">
            <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2 p-4 text-start"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-label={t("ADMIN_VENDOREDIT.OVERRIDES.TOGGLE")}
            >
                <div className="flex-1">
                    <p className="text-sm font-medium">{t("ADMIN_VENDOREDIT.OVERRIDES.TITLE")}</p>
                    <p className="text-xs text-muted-foreground">
                        {basePlan?.name
                            ? t("ADMIN_VENDOREDIT.OVERRIDES.DESCRIPTION_PLAN", { plan: basePlan.name })
                            : t("ADMIN_VENDOREDIT.OVERRIDES.DESCRIPTION")}
                    </p>
                </div>
                {overrideCount > 0 && (
                    <Badge variant="outline" className="bg-background">
                        {t("ADMIN_VENDOREDIT.OVERRIDES.COUNT", { count: overrideCount })}
                    </Badge>
                )}
                {open ? (
                    <ChevronUp className="size-4 text-muted-foreground" />
                ) : (
                    <ChevronDown className="size-4 text-muted-foreground" />
                )}
            </button>

            {open && (
                <div className="border-t p-4">
                    <p className="mb-1 text-[13px] font-medium">{t("ADMIN_VENDOREDIT.OVERRIDES.LIMITS")}</p>
                    <p className="mb-3 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.OVERRIDES.LIMITS_HINT")}</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {LIMIT_FIELDS.map((key) => {
                            const label = t(`ADMIN_VENDOREDIT.OVERRIDES.LIMIT.${key}`);
                            const isOverridden = limits[key] !== undefined && limits[key] !== null;
                            const inherited = inheritedLimit(key);
                            return (
                                <div key={key} className="flex items-center gap-2">
                                    <Switch
                                        checked={isOverridden}
                                        aria-label={t("ADMIN_VENDOREDIT.OVERRIDES.OVERRIDE_LABEL", { label })}
                                        onCheckedChange={(checked) =>
                                            setLimit(key, checked ? (inherited ?? 0) : undefined)
                                        }
                                    />
                                    <div className="flex-1">
                                        <p className="text-[13px]">{label}</p>
                                        {!isOverridden && (
                                            <p className="text-xs text-muted-foreground">
                                                {inherits(
                                                    inherited === -1
                                                        ? t("ADMIN_VENDOREDIT.OVERRIDES.UNLIMITED")
                                                        : inherited != null ? String(inherited) : "—",
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    {isOverridden && (
                                        <Input
                                            type="number"
                                            min={-1}
                                            value={limits[key]}
                                            aria-label={t("ADMIN_VENDOREDIT.OVERRIDES.VALUE_LABEL", { label })}
                                            onChange={(e) =>
                                                setLimit(key, e.target.value === "" ? 0 : Number(e.target.value))
                                            }
                                            className="h-8 w-24 bg-background text-[13px] tabular-nums"
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-5 mb-2 text-[13px] font-medium">{t("ADMIN_VENDOREDIT.OVERRIDES.FEATURES")}</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {FEATURE_FIELDS.map((key) => {
                            const label = t(`ADMIN_VENDOREDIT.OVERRIDES.FEATURE.${key}`);
                            const raw = features[key];
                            const state = raw === undefined || raw === null ? "inherit" : raw ? "on" : "off";
                            const inherited = inheritedFeature(key);
                            return (
                                <div key={key} className="flex items-center gap-2">
                                    <div className="flex-1">
                                        <p className="text-[13px]">{label}</p>
                                        {state === "inherit" && (
                                            <p className="text-xs text-muted-foreground">
                                                {inherits(
                                                    inherited === undefined
                                                        ? "—"
                                                        : inherited
                                                            ? t("ADMIN_VENDOREDIT.OVERRIDES.ON")
                                                            : t("ADMIN_VENDOREDIT.OVERRIDES.OFF"),
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    <Select
                                        value={state}
                                        onValueChange={(v) => {
                                            setFeature(key, v === "inherit" ? undefined : v === "on");
                                        }}
                                    >
                                        <SelectTrigger
                                            size="sm"
                                            className="min-w-28 bg-background"
                                            aria-label={t("ADMIN_VENDOREDIT.OVERRIDES.FEATURE_OVERRIDE_LABEL", { label })}
                                            data-testid={`feature-${key}`}
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="inherit">{t("ADMIN_VENDOREDIT.OVERRIDES.INHERIT")}</SelectItem>
                                            <SelectItem value="on">{t("ADMIN_VENDOREDIT.OVERRIDES.ON")}</SelectItem>
                                            <SelectItem value="off">{t("ADMIN_VENDOREDIT.OVERRIDES.OFF")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            );
                        })}

                        {/* visualizationQuality is an enum, not a boolean */}
                        <div className="flex items-center gap-2">
                            <div className="flex-1">
                                <p className="text-[13px]">{t("ADMIN_VENDOREDIT.OVERRIDES.VISUALIZATION_QUALITY")}</p>
                                {(features.visualizationQuality === undefined ||
                                    features.visualizationQuality === null) && (
                                    <p className="text-xs text-muted-foreground">
                                        {inherits(qualityLabel(basePlan?.features?.visualizationQuality))}
                                    </p>
                                )}
                            </div>
                            <Select
                                value={features.visualizationQuality ?? "inherit"}
                                onValueChange={(v) => {
                                    setFeature("visualizationQuality", v === "inherit" ? undefined : v);
                                }}
                            >
                                <SelectTrigger
                                    size="sm"
                                    className="min-w-28 bg-background"
                                    aria-label={t("ADMIN_VENDOREDIT.OVERRIDES.FEATURE_OVERRIDE_LABEL", {
                                        label: t("ADMIN_VENDOREDIT.OVERRIDES.VISUALIZATION_QUALITY"),
                                    })}
                                    data-testid="feature-visualizationQuality"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="inherit">{t("ADMIN_VENDOREDIT.OVERRIDES.INHERIT")}</SelectItem>
                                    {QUALITIES.map((q) => (
                                        <SelectItem key={q} value={q}>
                                            {t(`ADMIN_VENDOREDIT.OVERRIDES.QUALITY_${q.toUpperCase()}`)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
